import { IdealCustomerProfile } from '../types';

export interface SearchTemplate {
  id: string;
  type: 'companies' | 'people';
  tag: string;
  title: string;
  description: string;
  query: string;
}

function extractIndustry(solutionProducts: string): string {
  const text = solutionProducts.toLowerCase();

  const industries: Record<string, string[]> = {
    'insurance': ['insurance', 'insurtech', 'underwriting', 'claims'],
    'healthcare': ['healthcare', 'health tech', 'medical', 'clinical', 'hospital', 'patient'],
    'fintech': ['fintech', 'financial', 'banking', 'payments', 'lending', 'credit'],
    'e-commerce': ['ecommerce', 'e-commerce', 'retail', 'shopping', 'marketplace'],
    'SaaS': ['saas', 'software', 'platform', 'cloud'],
    'AI/ML': ['ai', 'artificial intelligence', 'machine learning', 'ml', 'deep learning'],
    'cybersecurity': ['security', 'cybersecurity', 'cyber', 'infosec'],
    'marketing': ['marketing', 'advertising', 'agency', 'brand', 'creative'],
    'HR tech': ['hr', 'human resources', 'recruiting', 'talent', 'hiring'],
    'real estate': ['real estate', 'property', 'proptech'],
    'logistics': ['logistics', 'supply chain', 'shipping', 'freight'],
    'education': ['education', 'edtech', 'learning', 'training'],
  };

  for (const [industry, keywords] of Object.entries(industries)) {
    if (keywords.some(kw => text.includes(kw))) {
      return industry;
    }
  }

  return 'B2B';
}

function extractProductType(solutionProducts: string): string {
  const text = solutionProducts.toLowerCase();

  if (text.includes('platform')) return 'platform';
  if (text.includes('api')) return 'API';
  if (text.includes('tool')) return 'tool';
  if (text.includes('software')) return 'software';
  if (text.includes('service')) return 'services';
  if (text.includes('solution')) return 'solution';
  if (text.includes('agency')) return 'agency services';

  return 'solution';
}

function getRegionSummary(regions: string[]): string {
  if (!regions || regions.length === 0) return '';
  if (regions.length === 1) return `in ${regions[0]}`;
  if (regions.length <= 3) return `in ${regions.slice(0, -1).join(', ')} and ${regions[regions.length - 1]}`;
  return `in ${regions[0]} and ${regions.length - 1} other regions`;
}

export function generateICPTemplates(icp: IdealCustomerProfile | null): SearchTemplate[] {
  if (!icp || !icp.solution_products) {
    return getDefaultTemplates();
  }

  const industry = extractIndustry(icp.solution_products);
  const productType = extractProductType(icp.solution_products);
  const regionSummary = getRegionSummary(icp.target_region);

  const templates: SearchTemplate[] = [];

  templates.push({
    id: '1',
    type: 'companies',
    tag: 'SALES',
    title: `Find ${industry} companies ready to buy`,
    description: `Discover companies in ${industry} that match your ideal customer profile and are showing buying signals.`,
    query: generateSalesQuery(industry, icp, regionSummary),
  });

  templates.push({
    id: '2',
    type: 'people',
    tag: 'RECRUITING',
    title: `Hire ${industry} specialists`,
    description: `Find experienced professionals who understand ${industry} and can help grow your ${productType}.`,
    query: generateRecruitingQuery(industry),
  });

  templates.push({
    id: '3',
    type: 'people',
    tag: 'INFLUENCERS',
    title: `Track ${industry} thought leaders`,
    description: `Find LinkedIn and Twitter influencers discussing ${industry} trends and engaging audiences.`,
    query: generateInfluencerQuery(industry),
  });

  templates.push({
    id: '4',
    type: 'companies',
    tag: 'MARKET INTEL',
    title: `Monitor ${industry} market moves`,
    description: `Track competitors, new entrants, and market shifts in the ${industry} space.`,
    query: generateMarketIntelQuery(industry, regionSummary),
  });

  return templates;
}

function generateSalesQuery(industry: string, icp: IdealCustomerProfile, regionSummary: string): string {
  const fundingContext = icp.funding_stages?.length
    ? ` at ${icp.funding_stages.slice(0, 2).join(' or ')} stage`
    : ' that recently raised funding';

  const sizeContext = icp.company_sizes?.length
    ? ` with ${icp.company_sizes[0]} employees`
    : ' with 50-500 employees';

  const regionContext = regionSummary || 'in North America';

  switch (industry) {
    case 'insurance':
      return `Find insurance and insurtech companies${fundingContext}${sizeContext} ${regionContext}, looking to modernize claims processing or underwriting, recently hired technology leadership, showing digital transformation signals`;
    case 'healthcare':
      return `Find healthcare and health tech organizations${fundingContext}${sizeContext} ${regionContext}, investing in digital transformation, HIPAA compliant, recently expanded their engineering team, actively adopting new technology`;
    case 'fintech':
      return `Find financial services and fintech companies${fundingContext}${sizeContext} ${regionContext}, adopting new payment or banking technology, recently hired VP of Engineering or CTO, showing product expansion signals`;
    case 'marketing':
      return `Find B2B companies ${regionContext}${sizeContext} actively hiring marketing agencies or expanding marketing team, recently raised funding, showing intent to scale demand generation or brand awareness`;
    case 'AI/ML':
      return `Find technology companies${fundingContext}${sizeContext} ${regionContext}, actively implementing AI or machine learning solutions, hiring ML engineers or data scientists, building AI-native products`;
    default:
      return `Find ${industry} companies${fundingContext}${sizeContext} ${regionContext}, showing growth signals, recently hired executives, expanding their team in the past 90 days`;
  }
}

function generateRecruitingQuery(industry: string): string {
  switch (industry) {
    case 'insurance':
      return `Find software engineers with insurance or insurtech experience, 3+ years, open to new opportunities`;
    case 'healthcare':
      return `Find engineers with healthcare compliance (HIPAA) experience at health tech startups`;
    case 'fintech':
      return `Find full-stack developers with fintech or payments experience at Series A-C startups`;
    case 'marketing':
      return `Find senior marketers with B2B SaaS experience, formerly at high-growth agencies`;
    case 'AI/ML':
      return `Find machine learning engineers with production AI experience at funded startups`;
    case 'cybersecurity':
      return `Find security engineers with cloud security experience, previously at enterprise companies`;
    default:
      return `Find experienced ${industry} professionals with startup background, open to new roles`;
  }
}

function generateInfluencerQuery(industry: string): string {
  switch (industry) {
    case 'insurance':
      return `Find LinkedIn influencers posting about insurtech, digital claims, or insurance innovation with 5k+ followers`;
    case 'healthcare':
      return `Find healthcare thought leaders on LinkedIn/Twitter discussing digital health, AI in medicine, or health tech trends`;
    case 'fintech':
      return `Find fintech influencers and analysts on Twitter/LinkedIn covering payments, neobanks, or embedded finance`;
    case 'marketing':
      return `Find marketing leaders and agency founders sharing growth strategies, with engaged audiences on LinkedIn`;
    case 'AI/ML':
      return `Find AI researchers and practitioners posting about LLMs, machine learning, or AI applications on Twitter/LinkedIn`;
    case 'cybersecurity':
      return `Find cybersecurity experts and CISOs sharing insights on LinkedIn about threats, compliance, or security trends`;
    default:
      return `Find industry influencers in ${industry} with 10k+ followers discussing trends and best practices`;
  }
}

function generateMarketIntelQuery(industry: string, regionSummary: string): string {
  const regionContext = regionSummary || '';

  switch (industry) {
    case 'insurance':
      return `Find insurtech startups ${regionContext} that launched new products or raised funding in the past 90 days`;
    case 'healthcare':
      return `Find digital health companies ${regionContext} announcing partnerships, funding, or product launches`;
    case 'fintech':
      return `Find fintech companies ${regionContext} expanding into new markets or launching competing products`;
    case 'marketing':
      return `Find marketing agencies ${regionContext} winning major accounts or expanding their service offerings`;
    case 'AI/ML':
      return `Find AI startups ${regionContext} launching products or announcing enterprise customers`;
    default:
      return `Find ${industry} companies ${regionContext} making strategic moves, hiring executives, or announcing funding`;
  }
}

function getDefaultTemplates(): SearchTemplate[] {
  return [
    {
      id: '1',
      type: 'companies',
      tag: 'SEARCH',
      title: 'Find companies',
      description: 'Simple search',
      query: 'Find companies that raised Series A in the last 3 months',
    },
    {
      id: '2',
      type: 'people',
      tag: 'SEARCH',
      title: 'Find with enrichment',
      description: 'Search + enrich',
      query: 'Find VP of Sales at fintech startups and get their verified emails',
    },
    {
      id: '3',
      type: 'companies',
      tag: 'WORKFLOW',
      title: 'Full pipeline',
      description: 'Search + enrich + outreach',
      query: 'Find companies hiring SDRs, get the hiring manager emails, and send a personalized intro about our sales tool',
    },
    {
      id: '4',
      type: 'companies',
      tag: 'WORKFLOW',
      title: 'Monitor + act',
      description: 'Continuous monitoring',
      query: 'Find my competitors and monitor them for pricing changes and new feature launches, then alert me with a summary',
    },
    {
      id: '5',
      type: 'companies',
      tag: 'WORKFLOW',
      title: 'Full automation',
      description: 'End-to-end workflow',
      query: 'Find target accounts that raised funding, research their expansion plans, find the new decision makers, enrich their contacts, and start an outreach sequence',
    },
    {
      id: '6',
      type: 'people',
      tag: 'WORKFLOW',
      title: 'Continuous pipeline',
      description: 'Loop with re-engagement',
      query: 'Find CTOs who posted about AI challenges on LinkedIn, send helpful content, monitor for replies, and re-engage non-responders after a week',
    },
    {
      id: '7',
      type: 'people',
      tag: 'WORKFLOW',
      title: 'Social listening',
      description: 'Social to meeting',
      query: 'Find posts on X and LinkedIn mentioning problems with our competitors, research the poster, and reach out with a personalized message',
    },
    {
      id: '8',
      type: 'companies',
      tag: 'WORKFLOW',
      title: 'Hiring signals',
      description: 'Job posts to pipeline',
      query: 'Find companies posting VP of Marketing roles, get the CEO and CFO emails, and send a warm intro',
    },
  ];
}

export function getSearchTemplates(icpProfiles: IdealCustomerProfile[] | null): SearchTemplate[] {
  if (!icpProfiles || icpProfiles.length === 0) {
    return getDefaultTemplates();
  }

  return generateICPTemplates(icpProfiles[0]);
}
